import React, { useContext, useEffect, useState } from 'react';

import {
	Button,
	Checkbox,
	Col,
	Drawer,
	Flex,
	Form,
	Input,
	Skeleton,
	Tabs,
	Tooltip,
	message,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import TextArea from 'antd/es/input/TextArea';

import { useUpdateQACollection } from '@/api/batch_process';
import { useGetSingleQACollectionById } from '@/api/monitor_collections';
import QAPromptItem from '@/components/common/QAPromptItem';
import { PageContext } from '@/providers/PageContextProvider';

export default function EditQACollectionDrawer() {
	const { drawer, closeDrawer } = useContext(PageContext);
	const [form] = useForm();
	const [tabActiveKey, setTabActiveKey] = useState('1');
	const [promptsErrIdxs, setPromptsErrIdxs] = useState<any>([]);

	const id = drawer?.data?.id;

	const {
		data: singleQACollectionData,
		isFetching: isSingleQACollectionFetching,
		isLoading: isSingleQACollectionLoading,
	} = useGetSingleQACollectionById({
		id,
		enabled: drawer?.isOpen && drawer.type === 'edit-qa-collection' && !!id,
	});

	const { mutate: updateQACollection, isLoading: isUpdateLoading } =
		useUpdateQACollection();

	let item = singleQACollectionData?.data?.data?.item;

	const onCancelClick = () => {
		closeDrawer();
		setTabActiveKey('1');
		form.resetFields();
		setPromptsErrIdxs([]);
	};

	const onSaveClick = async () => {
		try {
			setPromptsErrIdxs([]);
			await form.validateFields();
			const { title, description, favourite, prompts } = form.getFieldsValue();
			updateQACollection(
				{
					id,
					title: title?.trim(),
					description: description ? description?.trim() : '',
					favourite,
					prompts: prompts?.length
						? prompts?.map((pItem: any) => {
								return {
									id: pItem?.id,
									title: pItem?.title?.trim(),
									prompt: pItem?.prompt?.trim(),
								};
						  })
						: [],
				},
				{
					onSuccess: () => {
						message.success('Updated');
						closeDrawer();
						form.resetFields();
						setTabActiveKey('1');
					},
					onError: (error) => {
						const err = error as any;
						message.error(
							`An error occured: ${
								err?.response?.data?.message || err?.message
							}.`,
						);
						console.error('error', error);
					},
				},
			);
		} catch (error) {
			console.log('error', error);
			message.error('Please fill all required fields');
			if (
				(error as any)?.errorFields?.some((item: any) =>
					item?.name?.includes('prompts'),
				)
			) {
				const promptsErrIdxs =
					[
						...(error as any)?.errorFields
							?.filter((item: any) => item?.name?.includes('prompts'))
							?.map((item: any) => item?.name[1])
							?.filter((item: any) => item !== undefined),
					].reduce((acc: any, curr: any) => {
						if (!acc.includes(curr)) {
							acc.push(curr);
						}
						return acc;
					}, []) || [];
				setPromptsErrIdxs(promptsErrIdxs);
			}
		}
	};

	useEffect(() => {
		form.resetFields();
		// item &&
		form.setFieldsValue({
			title: item?.title || '',
			description: item?.description || '',
			favourite: item?.favourite || false,
			prompts: item?.prompts || [],
		});
	}, [form, item]);

	return (
		<Drawer
			open={drawer.isOpen && drawer.type === 'edit-qa-collection'}
			width={'650px'}
			onClose={onCancelClick}
			title="Edit QA Collection"
		>
			{isSingleQACollectionLoading || isSingleQACollectionFetching ? (
				<Flex justify="center" align="flex-start" style={{ height: '100%' }}>
					<Skeleton active paragraph={{ rows: 10 }} />
				</Flex>
			) : (
				<>
					<Col span={24}>
						<Flex
							justify="end"
							align="center"
							gap={20}
							style={{ marginBottom: '1.25rem' }}
						>
							<Button onClick={onCancelClick}>Cancel</Button>
							<Button
								type="primary"
								onClick={onSaveClick}
								loading={isUpdateLoading}
								disabled={
									isSingleQACollectionFetching || isSingleQACollectionLoading
								}
							>
								Save
							</Button>
						</Flex>
					</Col>
					<Form
						layout="vertical"
						form={form}
						initialValues={{
							title: item?.title,
							description: item?.description,
							favourite: item?.favourite ? true : false,
							prompts: item?.prompts,
						}}
						style={{ marginBottom: '1rem' }}
					>
						<Tabs
							defaultActiveKey="1"
							onChange={(key) => setTabActiveKey(key)}
							activeKey={tabActiveKey}
							style={{ padding: '0 1rem' }}
							items={[
								{
									key: '1',
									label: 'General',
									forceRender: true,
									children: (
										<Flex vertical>
											<Form.Item
												layout="vertical"
												label="Title"
												name="title"
												rules={[
													{ required: true, message: 'Title is required.' },
													{
														whitespace: true,
														message: 'Title cannot be empty.',
													},
												]}
												// initialValue={data?.name}
												// wrapperCol={{ offset: 6, span: 16 }}
												// onMetaChange={}
												// style={{ margin: 0 }}
											>
												<Input />
											</Form.Item>

											<Form.Item
												layout="vertical"
												label="Description"
												name="description"
												// initialValue={data?.description}
												// style={{ marginBottom: "3rem" }}
											>
												<TextArea rows={2} style={{ resize: 'none' }} />
											</Form.Item>

											{/* {data?.active !== undefined && ( */}
											<Tooltip>
												<Form.Item
													layout="vertical"
													name="favourite"
													valuePropName="checked"
													style={{ marginBottom: '1rem' }}
												>
													<Checkbox>favourite</Checkbox>
												</Form.Item>
											</Tooltip>
										</Flex>
									),
								},
								{
									key: '2',
									label: 'Prompts',
									forceRender: true,
									children: (
										<Flex vertical gap={8} align="stretch">
											<Form.List name="prompts">
												{(fields, { add, remove }) => (
													<>
														{fields.map(({ key, name, ...restField }) => (
															<QAPromptItem
																keyProp={key + name}
																name={name}
																restField={restField}
																deleteDisabled={false}
																hasError={promptsErrIdxs?.includes(name)}
																remove={remove}
																key={`qa-prompt-item-${key + name}`}
															/>
														))}
														<Form.Item
															style={{
																display: 'flex',
																justifyContent: 'center',
															}}
														>
															<Button
																type="dashed"
																onClick={() => add()}
																disabled={fields.length >= 3}
															>
																Add Prompt
															</Button>
														</Form.Item>
													</>
												)}
											</Form.List>
										</Flex>
									),
								},
							]}
						></Tabs>
					</Form>
				</>
			)}
		</Drawer>
	);
}
